import React, { FC, ReactNode, useMemo, useState } from 'react';

import { Popover } from '@mui/material';

import ComponentTypesIconWrapper from '../../../../../components/componentTypesIcon/ComponentTypesIconWrapper';
//components
import LottieInsert from '../../../../../components/shared/AampeLottieDisplay/LottieInsert';
import styled from 'styled-components';

import { LABELING_YOUR_CONTENT } from '../../../../../constants/aampeDocsUrls';
import aampeTheme from '../../../../../constants/aampeTheme';
import ANIMATION from '../../../../../constants/animation';
//constants
import ICONS from '../../../../../constants/icons';

import useComponentTypesQuery from '../../../../../hooks/component-types/useComponentTypesQuery';

import { ComponentTypeMapped } from '../../../../../models/component-types-model';
//models
import { GamificationItemModel } from '../../../../../models/label.model';

import LabelAgentIcon from './LabelAgentIcon';
import LabelGamificationList from './LabelGamificationList';

interface LabelGamificationTrayProps {
    selectedElement: { [key: string]: any };
    inProgressGamificationList: GamificationItemModel[];
    alternateVariantChangeIcon?: ReactNode;
    isLoading?: boolean;
    isEditable?: boolean;
}
const LabelGamificationTray: FC<LabelGamificationTrayProps> = ({
    selectedElement,
    inProgressGamificationList,
    alternateVariantChangeIcon,
    isLoading,
    isEditable,
}) => {
    const { data: componentTypes } = useComponentTypesQuery();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const closePopover = () => {
        setAnchorEl(null);
    };

    const totalBarsFilled = useMemo<number>(() => {
        return inProgressGamificationList.reduce(
            (total, item) => total + item.count,
            0,
        );
    }, [inProgressGamificationList]);

    const componentTypeData = useMemo(
        () => componentTypes?.map?.[selectedElement?.variantType as string],
        [selectedElement?.variantType, componentTypes],
    );

    const Icon = useMemo(() => {
        return (
            selectedElement && (
                <ComponentTypesIconWrapper
                    dataKey={
                        (componentTypeData as ComponentTypeMapped)?.data_key
                    }
                />
            )
        );
    }, [selectedElement, componentTypeData]);

    return (
        <SliderContainer>
            <IconContainer>
                <LabelAgentIcon
                    setAnchorEl={setAnchorEl}
                    inProgressGamificationList={inProgressGamificationList}
                    isLoading={isLoading}
                />
                {alternateVariantChangeIcon &&
                    isEditable &&
                    alternateVariantChangeIcon}
            </IconContainer>

            <Popover
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={closePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    style: {
                        width: '400px',
                        overflowY: 'auto',
                        transform: 'none',
                    },
                }}
            >
                <StyledPaper>
                    <HeaderWrapper>
                        <Text>
                            <ICONS.labelAgent03 width={40} height={40} />
                            <PopoverHeader>How do labels Work?</PopoverHeader>
                        </Text>
                        <CloseButtonContainer onClick={closePopover}>
                            <ICONS.close />
                        </CloseButtonContainer>
                    </HeaderWrapper>

                    <StyledTypography>
                        With at least 3 alternates for each label, Aampe is able
                        to learn about your audience.{' '}
                        <Link
                            href={LABELING_YOUR_CONTENT}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {' '}
                            <Text>Learn more</Text>
                        </Link>
                    </StyledTypography>

                    <HeaderText>
                        <VariantText>
                            {Icon}
                            {componentTypeData?.name}
                        </VariantText>
                        <LabelText>
                            {`${totalBarsFilled} of ${
                                inProgressGamificationList.length * 3
                            }`}{' '}
                            complete
                        </LabelText>
                    </HeaderText>

                    <ScrollableContainer>
                        {!inProgressGamificationList.length ? (
                            <StyledLottieInsert
                                lottie={ANIMATION.robotTwo}
                                text="Label alternates to help your Agents maximize their efficiency!"
                            />
                        ) : (
                            <LabelGamificationList
                                gamificationItemList={
                                    inProgressGamificationList
                                }
                                color={componentTypeData?.base_color ?? ''}
                            />
                        )}
                    </ScrollableContainer>
                </StyledPaper>
            </Popover>
        </SliderContainer>
    );
};

const IconContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
});

const LabelText = styled.span({
    fontSize: aampeTheme.size.font.default,
    fontWeight: 500,
    lineHeight: '24px',
    color: aampeTheme.colors.neutral09,
    marginRight: aampeTheme.spacing4(2),
});

const Text = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(5),
});

const VariantText = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
});
const HeaderText = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: aampeTheme.spacing4(2),
    padding: aampeTheme.spacing4(4, 0),
    fontSize: aampeTheme.size.font.textMedium,
    lineHeight: '24px',
    fontWeight: 500,
    color: aampeTheme.colors.textAccessibleWG01,
    borderTop: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderBottom: `1px solid ${aampeTheme.colors.bordersWG01}`,
    marginTop: aampeTheme.spacing4(3),
});

const SliderContainer = styled.div({
    height: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(4),
    flexDirection: 'column',
    overflow: 'auto',
});
const StyledPaper = styled.div({
    padding: aampeTheme.spacing4(5),
});
const PopoverHeader = styled.div({
    fontSize: aampeTheme.size.font.title,
    fontWeight: 500,
    lineHeight: '28px',
    color: aampeTheme.colors.neutral09,
});

const StyledTypography = styled.div({
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: 400,
    lineHeight: '28px',
    color: aampeTheme.colors.textAccessibleWG01,
    marginTop: aampeTheme.spacing4(3),
    position: 'relative',
});
const HeaderWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const CloseButtonContainer = styled.div({
    display: 'flex',
    justifyContent: 'flex-end',
    cursor: 'pointer',
    padding: aampeTheme.spacing4(2),
});

const ScrollableContainer = styled.div({
    maxHeight: '528px',
    overflowY: 'auto',
    marginTop: aampeTheme.spacing4(4),
});

const StyledLottieInsert = styled(LottieInsert)({
    display: 'flex',
    padding: aampeTheme.spacing4(1),
    alignItems: 'center',
    gap: aampeTheme.spacing4(4),
    overflow: 'hidden',
});

const Link = styled.a(() => ({
    textDecoration: 'none',
    display: 'inline-block',
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.solidA02,
    fontWeight: 400,
}));

export default LabelGamificationTray;
